import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, Building2, Users, Phone, Mail, MapPin, Clock, 
  DollarSign, TrendingUp, Briefcase, Edit, Trash2, Plus, Save, X,
  FileText, Calendar, CheckCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useDivision } from '@/context/DivisionContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEPARTMENTS = ['Corporate', 'Field', 'Estimating', 'Accounting', 'Operations', 'Other'];
const COMPANY_SIZES = [
  { value: 'small', label: 'Small (< 50 employees)' },
  { value: 'mid', label: 'Mid-size (50-250 employees)' },
  { value: 'large', label: 'Large (250+ employees)' }
];

const ContractorProfile = () => {
  const { contractorId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { division } = useDivision();
  
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    title: '',
    department: '',
    phone: '',
    email: '',
    is_primary: false
  });

  useEffect(() => {
    let isMounted = true;
    
    if (contractorId) {
      const loadData = async () => {
        try {
          const response = await axios.get(`${API}/contractors/${contractorId}`, {
            headers: getAuthHeaders()
          });
          if (isMounted) {
            setContractor(response.data);
            setEditData(response.data);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching contractor:', error);
          if (isMounted) {
            toast.error('Failed to load contractor');
            setLoading(false);
          }
        }
      };
      loadData();
    }
    
    return () => { isMounted = false; };
  }, [contractorId, getAuthHeaders]);

  const refreshContractor = async () => {
    try {
      const response = await axios.get(`${API}/contractors/${contractorId}`, {
        headers: getAuthHeaders()
      });
      setContractor(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Error fetching contractor:', error);
      toast.error('Failed to refresh contractor');
    }
  };

  const handleSave = async () => {
    try {
      await axios.patch(`${API}/contractors/${contractorId}`, editData, {
        headers: getAuthHeaders()
      });
      toast.success('Contractor updated successfully!');
      setEditing(false);
      refreshContractor();
    } catch (error) {
      console.error('Error updating contractor:', error);
      toast.error('Failed to update contractor');
    }
  };

  const handleAddContact = () => {
    if (!newContact.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    
    const contacts = editData.contacts || [];
    const isPrimary = contacts.length === 0 || newContact.is_primary;
    
    // If setting as primary, remove primary from others
    const updatedContacts = isPrimary 
      ? contacts.map(c => ({ ...c, is_primary: false }))
      : [...contacts];
    
    setEditData({
      ...editData,
      contacts: [...updatedContacts, { ...newContact, is_primary: isPrimary, id: Date.now().toString() }]
    });
    
    setNewContact({
      name: '',
      title: '',
      department: '',
      phone: '',
      email: '',
      is_primary: false
    });
    setAddContactDialogOpen(false);
  };

  const handleRemoveContact = (contactId) => {
    const contacts = editData.contacts.filter(c => c.id !== contactId);
    // If we removed the primary contact and there are still contacts, make the first one primary
    if (editData.contacts.find(c => c.id === contactId)?.is_primary && contacts.length > 0) {
      contacts[0].is_primary = true;
    }
    setEditData({ ...editData, contacts });
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Project deleted successfully');
      refreshContractor(); // Refresh the contractor data
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  if (loading) {
    return (
      <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
        <div className="text-center py-12 text-[#94a3b8]">Loading contractor...</div>
      </div>
    );
  }

  if (!contractor) {
    return (
      <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
        <div className="text-center py-12">
          <p className="text-[#94a3b8] text-lg mb-4">Contractor not found</p>
          <Button onClick={() => navigate('/contractors')} className="bg-[#f97316]">
            Back to Contractors
          </Button>
        </div>
      </div>
    );
  }

  const metrics = contractor.metrics || {};
  const paymentMetrics = contractor.payment_metrics || {};

  return (
    <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/contractors')}
              className="text-[#94a3b8] hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white">{contractor.name}</h1>
              {contractor.company_size && (
                <span className="text-sm text-[#94a3b8]">
                  {contractor.company_size.charAt(0).toUpperCase() + contractor.company_size.slice(1)} Company
                  {contractor.years_in_business && ` • ${contractor.years_in_business} years in business`}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setEditing(false); setEditData(contractor); }}
                  className="border-[#94a3b8] text-[#94a3b8]"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} className="bg-[#10b981] hover:bg-[#059669]">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </>
            ) : (
              <Button onClick={() => setEditing(true)} className="bg-[#f97316] hover:bg-[#ea580c]">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="millmetrix-card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#f97316]/20 rounded-lg">
                <Briefcase className="w-5 h-5 text-[#f97316]" />
              </div>
              <div>
                <p className="text-xs text-[#94a3b8] uppercase">Total Projects</p>
                <p className="text-2xl font-bold text-white">{metrics.total_projects || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="millmetrix-card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#10b981]/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-[#10b981]" />
              </div>
              <div>
                <p className="text-xs text-[#94a3b8] uppercase">Total Revenue</p>
                <p className="text-2xl font-bold text-[#10b981]">{formatCurrency(metrics.total_revenue)}</p>
              </div>
            </div>
          </Card>
          <Card className="millmetrix-card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#0ea5e9]/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-[#0ea5e9]" />
              </div>
              <div>
                <p className="text-xs text-[#94a3b8] uppercase">Avg Project Value</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(metrics.avg_project_value)}</p>
              </div>
            </div>
          </Card>
          <Card className="millmetrix-card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#8b5cf6]/20 rounded-lg">
                <Clock className="w-5 h-5 text-[#8b5cf6]" />
              </div>
              <div>
                <p className="text-xs text-[#94a3b8] uppercase">Avg Days to Pay</p>
                <p className="text-2xl font-bold text-white">
                  {paymentMetrics.avg_days_to_pay !== null ? `${paymentMetrics.avg_days_to_pay} days` : 'N/A'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Company Info & Contacts */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Information */}
            <Card className="millmetrix-card p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#f97316]" />
                Company Information
              </h2>
              
              {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#94a3b8]">Company Name</Label>
                    <Input
                      value={editData.name || ''}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value.toUpperCase() })}
                      className="millmetrix-input mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#94a3b8]">Years in Business</Label>
                    <Input
                      type="number"
                      value={editData.years_in_business || ''}
                      onChange={(e) => setEditData({ ...editData, years_in_business: parseInt(e.target.value) || null })}
                      className="millmetrix-input mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#94a3b8]">Company Size</Label>
                    <Select
                      value={editData.company_size || ''}
                      onValueChange={(value) => setEditData({ ...editData, company_size: value })}
                    >
                      <SelectTrigger className="millmetrix-input mt-1">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1e293b] border-[rgba(255,255,255,0.1)]">
                        {COMPANY_SIZES.map(size => (
                          <SelectItem key={size.value} value={size.value} className="text-white hover:bg-[#334155]">
                            {size.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#94a3b8]">Annual Revenue</Label>
                    <Input
                      type="number"
                      value={editData.annual_revenue || ''}
                      onChange={(e) => setEditData({ ...editData, annual_revenue: parseFloat(e.target.value) || null })}
                      className="millmetrix-input mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-[#94a3b8]">Address</Label>
                    <Input
                      value={editData.address || ''}
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                      className="millmetrix-input mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#94a3b8]">City</Label>
                    <Input
                      value={editData.city || ''}
                      onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                      className="millmetrix-input mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#94a3b8]">State</Label>
                    <Input
                      value={editData.state || ''}
                      onChange={(e) => setEditData({ ...editData, state: e.target.value.toUpperCase() })}
                      className="millmetrix-input mt-1"
                      maxLength={2}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-[#94a3b8]">Service Areas</Label>
                    <Input
                      value={(editData.service_areas || []).join(', ')}
                      onChange={(e) => setEditData({ 
                        ...editData, 
                        service_areas: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                      })}
                      className="millmetrix-input mt-1"
                      placeholder="Houston, Austin, Dallas"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-[#94a3b8]">Notes</Label>
                    <textarea
                      value={editData.notes || ''}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      className="millmetrix-input mt-1 min-h-[80px] resize-none w-full"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {(contractor.address || contractor.city) && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-[#94a3b8] mt-1" />
                      <div>
                        {contractor.address && <p className="text-white">{contractor.address}</p>}
                        {contractor.city && (
                          <p className="text-[#94a3b8]">{contractor.city}, {contractor.state} {contractor.zip_code}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {contractor.annual_revenue && (
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-4 h-4 text-[#94a3b8]" />
                      <span className="text-white">Est. Annual Revenue: {formatCurrency(contractor.annual_revenue)}</span>
                    </div>
                  )}
                  
                  {contractor.service_areas && contractor.service_areas.length > 0 && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-[#94a3b8] mt-1" />
                      <div>
                        <p className="text-xs text-[#94a3b8] uppercase mb-1">Service Areas</p>
                        <p className="text-white">{contractor.service_areas.join(', ')}</p>
                      </div>
                    </div>
                  )}
                  
                  {contractor.notes && (
                    <div className="mt-4 p-3 bg-[#0f172a] rounded-lg">
                      <p className="text-xs text-[#94a3b8] uppercase mb-1">Notes</p>
                      <p className="text-white text-sm">{contractor.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Contacts */}
            <Card className="millmetrix-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#f97316]" />
                  Contacts ({(editing ? editData.contacts : contractor.contacts)?.length || 0})
                </h2>
                {editing && (
                  <Button
                    size="sm"
                    onClick={() => setAddContactDialogOpen(true)}
                    className="bg-[#10b981] hover:bg-[#059669]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Contact
                  </Button>
                )}
              </div>
              
              <div className="space-y-3">
                {((editing ? editData.contacts : contractor.contacts) || []).map((contact, index) => (
                  <div 
                    key={contact.id || index} 
                    className="p-4 bg-[#0f172a] rounded-lg border border-[rgba(255,255,255,0.05)]"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-white">{contact.name}</span>
                          {contact.is_primary && (
                            <span className="text-xs bg-[#f97316] text-white px-2 py-0.5 rounded">Primary</span>
                          )}
                        </div>
                        {contact.title && (
                          <p className="text-sm text-[#94a3b8]">
                            {contact.title}
                            {contact.department && ` • ${contact.department}`}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-2">
                          {contact.phone && (
                            <div className="flex items-center gap-1 text-sm text-[#94a3b8]">
                              <Phone className="w-3 h-3" />
                              <span>{contact.phone}</span>
                            </div>
                          )}
                          {contact.email && (
                            <div className="flex items-center gap-1 text-sm text-[#94a3b8]">
                              <Mail className="w-3 h-3" />
                              <span>{contact.email}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {editing && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveContact(contact.id)}
                          className="text-red-500 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                
                {((editing ? editData.contacts : contractor.contacts) || []).length === 0 && (
                  <p className="text-center text-[#94a3b8] py-4">No contacts added yet</p>
                )}
              </div>
            </Card>
          </div>

          {/* Right Column - Payment Info & Recent Projects */}
          <div className="space-y-6">
            {/* Payment Metrics */}
            <Card className="millmetrix-card p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#10b981]" />
                Payment Behavior
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#94a3b8]">Avg Days to Pay</span>
                  <span className={`font-bold ${paymentMetrics.avg_days_to_pay && paymentMetrics.avg_days_to_pay > 30 ? 'text-yellow-500' : 'text-[#10b981]'}`}>
                    {paymentMetrics.avg_days_to_pay !== null ? `${paymentMetrics.avg_days_to_pay} days` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#94a3b8]">Payment Reliability</span>
                  <span className={`font-bold ${paymentMetrics.payment_reliability && paymentMetrics.payment_reliability < 80 ? 'text-yellow-500' : 'text-[#10b981]'}`}>
                    {paymentMetrics.payment_reliability !== null ? `${paymentMetrics.payment_reliability}%` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#94a3b8]">Payments Tracked</span>
                  <span className="font-bold text-white">{paymentMetrics.total_payments_tracked || 0}</span>
                </div>
                
                {paymentMetrics.total_payments_tracked < 3 && (
                  <p className="text-xs text-[#64748b] mt-2">
                    *Reliability calculated after 3+ payments
                  </p>
                )}
              </div>
            </Card>

            {/* Project Summary */}
            <Card className="millmetrix-card p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-[#f97316]" />
                Project Summary
              </h2>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-[#0f172a] rounded-lg">
                  <span className="text-[#94a3b8]">Active</span>
                  <span className="font-bold text-[#10b981]">{metrics.active_projects || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-[#0f172a] rounded-lg">
                  <span className="text-[#94a3b8]">Invoiced (AR)</span>
                  <span className="font-bold text-[#f97316]">{metrics.invoiced_projects || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-[#0f172a] rounded-lg">
                  <span className="text-[#94a3b8]">Paid/Completed</span>
                  <span className="font-bold text-white">{metrics.paid_projects || 0}</span>
                </div>
              </div>
            </Card>

            {/* Recent Projects */}
            {contractor.recent_projects && contractor.recent_projects.length > 0 && (
              <Card className="millmetrix-card p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#f97316]" />
                  Recent Projects
                </h2>
                
                <div className="space-y-3">
                  {contractor.recent_projects.slice(0, 5).map((project) => (
                    <div 
                      key={project.id} 
                      className="p-3 bg-[#0f172a] rounded-lg border border-[rgba(255,255,255,0.05)] group"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-white text-sm">{project.project_name}</p>
                          <p className="text-xs text-[#94a3b8]">{project.control_number}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            project.status === 'active' ? 'bg-[#10b981]/20 text-[#10b981]' :
                            project.status === 'invoiced' ? 'bg-[#f97316]/20 text-[#f97316]' :
                            'bg-[#94a3b8]/20 text-[#94a3b8]'
                          }`}>
                            {project.status}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project.id, project.project_name);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:bg-red-500/20 h-7 w-7 p-0"
                            title="Delete project"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-xs text-[#64748b]">{formatDate(project.created_at)}</span>
                        <span className="text-sm font-semibold text-[#10b981]">{formatCurrency(project.bid_amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Add Contact Dialog */}
      <Dialog open={addContactDialogOpen} onOpenChange={setAddContactDialogOpen}>
        <DialogContent className="bg-[#1e293b] text-white border-[rgba(255,255,255,0.1)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#10b981]" />
              Add Contact
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs text-[#94a3b8]">Contact Name *</Label>
              <Input
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                className="millmetrix-input mt-1"
                placeholder="John Smith"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#94a3b8]">Title</Label>
                <Input
                  value={newContact.title}
                  onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                  className="millmetrix-input mt-1"
                  placeholder="Estimator"
                />
              </div>
              <div>
                <Label className="text-xs text-[#94a3b8]">Department</Label>
                <Select
                  value={newContact.department}
                  onValueChange={(value) => setNewContact({ ...newContact, department: value })}
                >
                  <SelectTrigger className="millmetrix-input mt-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e293b] border-[rgba(255,255,255,0.1)]">
                    {DEPARTMENTS.map(dept => (
                      <SelectItem key={dept} value={dept} className="text-white hover:bg-[#334155]">
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#94a3b8]">Phone</Label>
              <Input
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                className="millmetrix-input mt-1"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <Label className="text-xs text-[#94a3b8]">Email</Label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                className="millmetrix-input mt-1"
                placeholder="john@company.com"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#94a3b8] cursor-pointer">
              <input
                type="checkbox"
                checked={newContact.is_primary}
                onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                className="w-4 h-4"
              />
              Set as Primary Contact
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddContactDialogOpen(false)}
              className="border-[#94a3b8] text-[#94a3b8]"
            >
              Cancel
            </Button>
            <Button onClick={handleAddContact} className="bg-[#10b981] hover:bg-[#059669]">
              Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractorProfile;
